// ─────────────────────────────────────────────────────────────────────────────
// Process Manufacturing — Manufacturing Dashboard Page
// Executive overview with KPI cards, active units, tank levels, WIP summary
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Factory, Gauge, TrendingUp, AlertTriangle, Package, Database, BarChart3, RefreshCw, ChevronRight, ClipboardList, Calculator, FlaskRound } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { useApi } from '@/shared/hooks/useApi';

interface KPICardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon: React.ReactNode;
  color: string;
  link?: string;
  index?: number;
}

function KPICard({ title, value, change, changeType = 'neutral', icon, color, link, index = 0 }: KPICardProps) {
  const handleClick = () => {
    if (link) window.location.href = link;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: index * 0.04 }}
      whileHover={{ y: -2 }}
      className="group h-full"
    >
      <Card className="p-5 h-full cursor-pointer transition-shadow duration-200 ease-standard hover:shadow-surface-3" onClick={handleClick}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 truncate">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{value}</p>
            {change && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${
                changeType === 'positive' ? 'text-emerald-600 dark:text-emerald-400' : changeType === 'negative' ? 'text-rose-600 dark:text-rose-400' : 'text-gray-500 dark:text-gray-400'
              }`}>
                <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{change}</span>
              </p>
            )}
          </div>
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 ease-standard group-hover:scale-110"
            style={{
              background: `linear-gradient(135deg, ${color}29, ${color}0D)`,
              boxShadow: `0 0 0 1px ${color}26`,
              color,
            }}
          >
            {icon}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

interface TankLevelCardProps {
  tank: any;
}

function TankLevelCard({ tank }: TankLevelCardProps) {
  const fillPercent = tank.maxVolume > 0 ? ((tank.currentVolume || 0) / tank.maxVolume) * 100 : 0;
  const isHigh = fillPercent > 90;
  const isLow = fillPercent < 10;

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>
    <Card className="p-4 h-full transition-shadow duration-200 ease-standard hover:shadow-surface-2">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{tank.code}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{tank.name}</p>
        </div>
        <Badge variant={tank.status === 'active' ? 'success' : 'secondary'}>{tank.status}</Badge>
      </div>
      
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-500 dark:text-gray-400">Fill Level</span>
          <span className="font-medium" style={{ color: isHigh ? '#ef4444' : isLow ? '#f59e0b' : '#22c55e' }}>
            {fillPercent.toFixed(1)}%
          </span>
        </div>
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${Math.min(100, fillPercent)}%`,
              backgroundColor: isHigh ? '#ef4444' : isLow ? '#f59e0b' : '#3b82f6'
            }}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
          <p className="text-gray-500 dark:text-gray-400">Volume</p>
          <p className="font-medium">{(tank.currentVolume || 0).toLocaleString()} L</p>
        </div>
        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
          <p className="text-gray-500 dark:text-gray-400">Mass</p>
          <p className="font-medium">{(tank.currentMass || 0).toLocaleString()} kg</p>
        </div>
        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
          <p className="text-gray-500 dark:text-gray-400">Temp</p>
          <p className="font-medium">{tank.currentTemp || 15}°C</p>
        </div>
      </div>
      
      {tank.currentApiGravity && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-xs">
          <p className="text-gray-500 dark:text-gray-400">API Gravity: <span className="font-medium">{tank.currentApiGravity}°API</span></p>
        </div>
      )}
    </Card>
    </motion.div>
  );
}

interface ActiveOrderCardProps {
  order: any;
}

function ActiveOrderCard({ order }: ActiveOrderCardProps) {
  const statusColors: Record<string, string> = {
    released: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}>
    <Card className="p-4 transition-shadow duration-200 ease-standard hover:shadow-surface-2">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{order.orderNumber}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{order.batchNumber}</p>
        </div>
        <Badge variant="secondary" className={statusColors[order.status] || ''}>{order.status}</Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <div>
          <p className="text-gray-500 dark:text-gray-400">Formula</p>
          <p className="font-medium">{order.formulaId || 'N/A'}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-gray-400">Target</p>
          <p className="font-medium">{(order.targetQty || 0).toLocaleString()} kg</p>
        </div>
      </div>
      
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span>Started: {order.actualStart ? new Date(order.actualStart).toLocaleDateString() : 'Not started'}</span>
        {order.actualEnd && <span>• Completed: {new Date(order.actualEnd).toLocaleDateString()}</span>}
      </div>
    </Card>
    </motion.div>
  );
}

export function ManufacturingDashboardPage() {
  const [kpis, setKpis] = useState<any[]>([]);
  const [tanks, setTanks] = useState<any[]>([]);
  const [activeOrders, setActiveOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { get } = useApi();
  
  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch data from API
        const [tanksRes, ordersRes, samplesRes, completedBatchesRes] = await Promise.all([
          get('/api/manufacturing/master/tanks?limit=10'),
          get('/api/manufacturing/orders?status=released&status=in_progress&limit=5'),
          get('/api/manufacturing/quality/samples'),
          get('/api/manufacturing/batch-records?status=completed'),
        ]);

        setTanks(tanksRes.tanks || []);
        setActiveOrders(ordersRes.orders || []);

        // Calculate KPIs
        const activeTanks = tanksRes.tanks?.filter((t: any) => t.status === 'active') || [];
        const totalVolume = activeTanks.reduce((sum: number, t: any) => sum + (t.currentVolume || 0), 0);
        const totalCapacity = activeTanks.reduce((sum: number, t: any) => sum + (t.maxVolume || 0), 0);
        const avgFill = totalCapacity > 0 ? (totalVolume / totalCapacity) * 100 : 0;

        const inProgressOrders = ordersRes.orders?.filter((o: any) => o.status === 'in_progress') || [];
        const releasedOrders = ordersRes.orders?.filter((o: any) => o.status === 'released') || [];

        // Quality: 'quarantine'/'testing' are the un-decided states — see
        // mfgQualitySampleSchema.status in src/db-manufacturing.js.
        const samples: any[] = samplesRes.samples || [];
        const pendingSamples = samples.filter((s) => s.status === 'quarantine' || s.status === 'testing');

        // Yield variance: real per-line yieldPct (actualQty vs plannedQty,
        // already computed server-side) averaged across every completed
        // batch's actual outputs. No completed batches means no honest
        // number to show — 'No completed batches yet' rather than a made-up
        // percentage.
        const completedBatches: any[] = completedBatchesRes.batchRecords || [];
        const yieldPcts = completedBatches.flatMap((b) => (b.actualOutputs || []).map((o: any) => o.yieldPct).filter((p: number) => typeof p === 'number'));
        const avgYieldVariance = yieldPcts.length > 0 ? (yieldPcts.reduce((s, p) => s + p, 0) / yieldPcts.length) - 100 : null;

        setKpis([
          // No real historical snapshot to compare tank counts against, so
          // this card carries no fabricated trend line — just the count.
          { title: 'Active Tanks', value: activeTanks.length, icon: <Gauge className="w-6 h-6" />, color: '#3b82f6', link: '/manufacturing/master-data' },
          { title: 'Avg Tank Fill', value: `${avgFill.toFixed(1)}%`, change: avgFill > 80 ? 'High utilization' : 'Normal', changeType: avgFill > 80 ? 'negative' : 'neutral', icon: <Database className="w-6 h-6" />, color: '#22c55e', link: '/manufacturing/master-data' },
          { title: 'Active Orders', value: inProgressOrders.length, change: `${releasedOrders.length} pending release`, changeType: 'neutral', icon: <Factory className="w-6 h-6" />, color: '#f59e0b', link: '/manufacturing/orders' },
          { title: 'WIP Batches', value: inProgressOrders.length, change: 'All batches tracked', changeType: 'positive', icon: <Package className="w-6 h-6" />, color: '#8b5cf6', link: '/manufacturing/batch-ops' },
          { title: 'Quality Samples', value: samples.length, change: `${pendingSamples.length} pending review`, changeType: pendingSamples.length > 0 ? 'negative' : 'positive', icon: <AlertTriangle className="w-6 h-6" />, color: '#ef4444', link: '/manufacturing/quality' },
          { title: 'Yield Variance', value: avgYieldVariance === null ? '—' : `${avgYieldVariance >= 0 ? '+' : ''}${avgYieldVariance.toFixed(1)}%`, change: avgYieldVariance === null ? 'No completed batches yet' : Math.abs(avgYieldVariance) <= 2 ? 'Within tolerance' : 'Outside tolerance', changeType: avgYieldVariance === null ? 'neutral' : Math.abs(avgYieldVariance) <= 2 ? 'positive' : 'negative', icon: <BarChart3 className="w-6 h-6" />, color: '#06b6d4', link: '/manufacturing/costing' },
        ]);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, [get]);
  
  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[1,2,3,4,5,6].map(i => (
            <Card key={i} className="p-6 h-32">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary-dim text-on-primary flex items-center justify-center shadow-surface-2 shrink-0">
            <Factory className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manufacturing Hub</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">Executive overview of process manufacturing operations</p>
          </div>
        </div>
        <Button onClick={() => window.location.reload()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpis.map((kpi, idx) => (
          <KPICard key={idx} index={idx} {...kpi} />
        ))}
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tank Levels - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Gauge className="w-5 h-5" />
                Tank Levels & Status
              </h2>
            </div>
            <div className="p-4">
              {tanks.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No tanks configured. <a href="/manufacturing/master-data" className="text-blue-600 hover:underline">Add tanks</a> to get started.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tanks.slice(0, 9).map((tank, idx) => (
                    <TankLevelCard key={tank._id || idx} tank={tank} />
                  ))}
                </div>
              )}
            </div>
          </Card>
          
          {/* Quick Actions */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Quick Actions
              </h2>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => window.location.href = '/manufacturing/orders'}>
                <ClipboardList className="w-6 h-6" />
                <span className="text-sm">Create Order</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => window.location.href = '/manufacturing/quality'}>
                <AlertTriangle className="w-6 h-6" />
                <span className="text-sm">Quality Check</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => window.location.href = '/manufacturing/costing'}>
                <Calculator className="w-6 h-6" />
                <span className="text-sm">Cost Rollup</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => window.location.href = '/manufacturing/packaging'}>
                <Package className="w-6 h-6" />
                <span className="text-sm">Packaging Run</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2" onClick={() => window.location.href = '/manufacturing/oil-scenario-test'}>
                <FlaskRound className="w-6 h-6" />
                <span className="text-sm">Oil Scenario Test</span>
              </Button>
            </div>
          </Card>
        </div>
        
        {/* Sidebar - Active Orders & Alerts */}
        <div className="space-y-6">
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Factory className="w-5 h-5" />
                Active Production Orders
              </h2>
              <a href="/manufacturing/orders" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                View all <ChevronRight className="w-3 h-3" />
              </a>
            </div>
            <div className="p-4 space-y-3">
              {activeOrders.length === 0 ? (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No active production orders
                </div>
              ) : (
                activeOrders.map((order, idx) => (
                  <ActiveOrderCard key={order._id || idx} order={order} />
                ))
              )}
            </div>
          </Card>
          
          {/* Recent Alerts */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Recent Alerts
              </h2>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Tank TANK-101 at 95% capacity</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Consider transferring to TANK-102 to prevent overfill</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <AlertTriangle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Quality sample QC-2026-088001 pending</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Diesel batch BATCH-2026-088 awaiting lab results</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">4 hours ago</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <AlertTriangle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">Cost allocation CA-BATCH-2026-088 approved</p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">NRV allocation completed for 4 co-products</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">1 day ago</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}